import {
  ArrowTopRightOnSquareIcon,
  ClockIcon,
  KeyIcon,
} from "@heroicons/react/24/outline";
import {
  ClipboardDocumentCheckIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/solid";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useContext, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";

import RefillSettingsCard, {
  ConvertToSubscriptionMetadata,
  SubscriptionMetadata,
} from "./RefillSettingsForm";

import { useApiKey } from "context/ApiKeyContext";
import UserContext from "context/UserContext";

import Badge from "components/atoms/Badge";
import Button from "components/atoms/Button";
import Card from "components/atoms/Card";
import Spinner from "components/atoms/Spinner";
import { convertToNumLetter } from "components/molecules/PriceTables/PriceTableNutritionAPI";

import { error, success } from "utils";
import { ProductDataType } from "utils/types";

// Tier display configuration
const tierDisplayNames: Record<string, string> = {
  trial: "Trial",
  mid: "Professional",
  high: "Enterprise",
};

const tierColors: Record<string, string> = {
  trial: "bg-amber-100 text-amber-800 border-amber-200",
  mid: "bg-blue-100 text-blue-800 border-blue-200",
  high: "bg-purple-100 text-purple-800 border-purple-200",
};

const DetailsTieredAIHub = () => {
  const { setApiKey } = useApiKey();
  const search = window.location.search;
  const params = new URLSearchParams(search);
  const productQuery = params.get("product");

  const [unauthorized, setUnauthorized] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [hideKey, setHideKey] = useState(true);

  const [productState, setProductState] = useState<ProductDataType>({
    isSubscribed: false,
    trialEnd: null,
    billingDate: new Date(),
    licenseKey: null,
    usageInfo: null,
    tiers: [],
  });

  const [productSubDetail, setProductSubDetail] = useState<any>(null);
  const [currentTier, setCurrentTier] = useState<string | null>(null);
  const [usedPercentage, setUsedPercentage] = useState(0);
  const [predictedPercentage, setPredictedPercentage] = useState(0);

  // Store the API key in context whenever it changes
  useEffect(() => {
    if (productState?.licenseKey) {
      setApiKey(productState.licenseKey);
    }
  }, [productState?.licenseKey, setApiKey]);

  const userContext = useContext(UserContext);

  const calculatePredictedUsage = (
    startDate: number,
    endDate: number,
    currentUsage: number
  ): number => {
    const now = new Date();
    const start = new Date(startDate * 1000);
    const end = new Date(endDate * 1000);

    const totalDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    let daysPassed = (now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    daysPassed = Math.max(daysPassed, 1);

    if (daysPassed <= 0) return currentUsage;

    const dailyRate = currentUsage / daysPassed;
    return dailyRate * totalDays;
  };

  useEffect(() => {
    if (productSubDetail?.stripeSubscription && productSubDetail.usageInfo) {
      const predicted = calculatePredictedUsage(
        productSubDetail.stripeSubscription.current_period_start,
        productSubDetail.stripeSubscription.current_period_end,
        productSubDetail.usageInfo.units
      );
      setPredictedPercentage(
        (predicted / productSubDetail.usageInfo.unitsQuota) * 100
      );
    }
  }, [productSubDetail]);

  // Fetch AI Hub subscription details
  const getProduct = async () =>
    await axios.get(`/accounts/products/ai-hub/detail`);

  const { isLoading, isFetching, refetch } = useQuery(
    ["ai-hub-product-detail"],
    getProduct,
    {
      enabled: !!productQuery && !!userContext?.customerid,
      onError: (err: AxiosError) => {
        error(err?.response?.data?.cause || "Could not fetch the products");
        if (err?.response?.status === 403) {
          setUnauthorized(true);
        }
      },
      onSuccess: (res: AxiosResponse) => {
        setProductSubDetail(res?.data);
        setUsedPercentage(
          res?.data?.usageInfo
            ? (res?.data?.usageInfo.units / res?.data?.usageInfo.unitsQuota) *
                100
            : 0
        );
        setCurrentTier(res?.data?.tier || null);
        setProductState((prev) => ({
          ...prev,
          isSubscribed: res.data?.isSubscribed,
          trialEnd: res.data?.trialEnd ? new Date(res.data?.trialEnd) : null,
          billingDate: new Date(res.data?.billingDate),
          licenseKey: res.data?.licenseKey,
          usageInfo: res.data?.usageInfo,
          tiers: res.data?.tiers || [],
        }));
      },
    }
  );

  async function copyTextToClipboard(text: string) {
    return await navigator.clipboard.writeText(text);
  }

  const handleCopyClick = () => {
    copyTextToClipboard(productState?.licenseKey ?? "").then(() => {
      setIsCopied(true);
      setTimeout(() => {
        setIsCopied(false);
      }, 1500);
    });
  };

  const isTrialTier = currentTier === "trial";
  const canRefill = currentTier && currentTier !== "trial";

  if (unauthorized) {
    return <Navigate to="/access-restricted" />;
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-bold inter-bold text-md md:text-lg xl:text-3xl">
          AI Hub Subscription Details
        </h2>
        {currentTier && (
          <span
            className={`px-4 py-2 rounded-full text-sm font-semibold border ${
              tierColors[currentTier] || "bg-gray-100 text-gray-800"
            }`}
          >
            {tierDisplayNames[currentTier] || currentTier} Tier
          </span>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-8 auto-rows-auto">
        {/* Usage Card */}
        <Card className="sm:p-6">
          <div className="flex justify-between">
            <div className="flex flex-col">
              <p className="font-medium">Current Period</p>
              <span className="text-sm text-gray-500">
                {productSubDetail?.stripeSubscription?.current_period_start &&
                  new Date(
                    productSubDetail.stripeSubscription.current_period_start *
                      1000
                  ).toLocaleDateString("default", {
                    month: "2-digit",
                    year: "2-digit",
                    day: "2-digit",
                  })}
                {"  -  "}
                {productSubDetail?.stripeSubscription?.current_period_end &&
                  new Date(
                    productSubDetail.stripeSubscription.current_period_end *
                      1000
                  ).toLocaleDateString("default", {
                    month: "2-digit",
                    year: "2-digit",
                    day: "2-digit",
                  })}
              </span>
            </div>
            {!isFetching && !isLoading ? (
              productState.trialEnd ? (
                <Badge isWarning>
                  <ClockIcon
                    className="h-5 w-5 flex-shrink-0 mr-2"
                    aria-hidden="true"
                  />
                  Free Trial Ends on{" "}
                  {productState.trialEnd.toLocaleDateString()}
                </Badge>
              ) : (
                <Badge>
                  <ClockIcon
                    className="h-5 w-5 flex-shrink-0 mr-2"
                    aria-hidden="true"
                  />
                  Billed Next on {productState.billingDate.toLocaleDateString()}
                </Badge>
              )
            ) : null}
          </div>

          <div className="flex flex-col gap-4 mt-6">
            {isLoading || isFetching ? (
              <Spinner className="!w-[2rem]" />
            ) : productSubDetail?.usageInfo?.units !== undefined ? (
              <>
                <div className="text-passio-indigo text-md font-bold text-center">
                  {convertToNumLetter(productSubDetail.usageInfo.units)} /{" "}
                  {convertToNumLetter(productSubDetail.usageInfo.unitsQuota)}
                </div>
                <div className="w-full bg-indigo-50 rounded-full h-6 relative overflow-hidden">
                  <div
                    className="absolute bg-indigo-100 h-6 rounded-full"
                    style={{
                      width: `${Math.min(predictedPercentage, 100)}%`,
                    }}
                  ></div>
                  <div
                    className="absolute bg-indigo-600 h-6 rounded-full"
                    style={{
                      width: `${Math.min(usedPercentage, 100)}%`,
                    }}
                  ></div>
                </div>
                <div className="flex justify-between w-full text-indigo-600">
                  <span className="text-sm font-medium">
                    {usedPercentage.toFixed(1)}% Used (
                    {predictedPercentage.toFixed(1)}% Projected)
                  </span>
                  {usedPercentage < 100 && (
                    <span className="text-sm font-medium">
                      {(100 - usedPercentage).toFixed(1)}% Available
                    </span>
                  )}
                </div>

                {/* Trial Tier Warning */}
                {isTrialTier && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-amber-800 text-sm">
                      <strong>Trial Plan:</strong> You are on the trial tier.
                      Refills are not available. When you use all your tokens,
                      you&apos;ll need to upgrade to continue using the service.
                    </p>
                    <Button
                      text="Upgrade Plan"
                      className="mt-3"
                      onClick={() =>
                        (window.location.href = `/manage-subscription?product=${productQuery}`)
                      }
                    />
                  </div>
                )}

                {/* Overage Warning */}
                {predictedPercentage > 100 && canRefill && (
                  <div className="mt-2 p-4 bg-red-100 rounded-md text-red-600">
                    <p>
                      Warning: You are projected to exceed your token quota.
                    </p>
                    <p>
                      {productSubDetail?.stripeSubscription?.metadata
                        ?.refillBehaviour === "auto"
                        ? "Auto-refill will purchase more tokens at $30 per 20M."
                        : "Consider enabling auto-refill or manually purchasing more tokens."}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-gray-500 text-center">
                No usage data available
              </p>
            )}
          </div>
        </Card>

        {/* API Key Card */}
        <Card className="sm:p-6">
          <div className="flex flex-col">
            <p className="font-medium">API Key & Info</p>
          </div>

          <div className="mt-6">
            <dt className="text-sm font-medium text-gray-500">API Key</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <KeyIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      {hideKey
                        ? Array.from({
                            length: productState?.licenseKey?.length || 0,
                          }).map((_, index) => <span key={index}>•</span>)
                        : productState?.licenseKey ?? ""}
                    </span>
                  </div>
                  <div className="ml-4 flex flex-row flex-shrink items-center space-x-2">
                    {isCopied ? (
                      <ClipboardDocumentCheckIcon
                        className="h-5 w-5 flex-shrink-0 text-green-700"
                        aria-hidden="true"
                      />
                    ) : (
                      <DocumentDuplicateIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        aria-hidden="true"
                        onClick={handleCopyClick}
                      />
                    )}
                    {hideKey ? (
                      <EyeSlashIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        aria-hidden="true"
                        onClick={() => setHideKey(!hideKey)}
                      />
                    ) : (
                      <EyeIcon
                        className="h-5 w-5 flex-shrink-0 text-passio-indigo cursor-pointer"
                        aria-hidden="true"
                        onClick={() => setHideKey(!hideKey)}
                      />
                    )}
                  </div>
                </li>
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <Link
                        to="/tools/mealplans"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Meal Planning Tool
                      </Link>
                    </span>
                  </div>
                </li>
              </ul>
              <p className="text-gray-500 text-sm ml-4 mt-1">
                * Keep your API Key safe - see the best practices in the
                documentation.
              </p>
            </dd>
          </div>

          <div className="pt-6">
            <dt className="text-sm font-medium text-gray-500">Quick Links</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <ul
                role="list"
                className="divide-y divide-gray-200 rounded-md border border-gray-200"
              >
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <a
                        href={"https://passio.gitbook.io/nutrition-ai"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        Documentation
                      </a>
                    </span>
                  </div>
                </li>
                <li className="flex items-center justify-between py-3 pl-3 pr-4 text-sm">
                  <div className="flex w-0 flex-1 items-center">
                    <ArrowTopRightOnSquareIcon
                      className="h-5 w-5 flex-shrink-0 text-gray-400"
                      aria-hidden="true"
                    />
                    <span className="ml-2 w-0 flex-1 truncate">
                      <a
                        href={"https://api.passiolife.com/v2/products/docs"}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-indigo-600 hover:text-indigo-500"
                      >
                        REST OpenAPI Documentation
                      </a>
                    </span>
                  </div>
                </li>
              </ul>
            </dd>
          </div>
        </Card>

        {/* Refill Settings - Only show if not trial tier */}
        {canRefill && (
          <div className="flex-grow">
            {isLoading || isFetching || !productSubDetail ? (
              <Spinner />
            ) : (
              <RefillSettingsCard
                subscriptionMetadata={ConvertToSubscriptionMetadata(
                  productSubDetail?.stripeSubscription?.metadata ||
                    ({} as SubscriptionMetadata)
                )}
                onRefill={async () => {
                  try {
                    await axios.post(
                      "/accounts/products/ai-hub/subscription/refill"
                    );
                    success("Refill initiated successfully");
                    await new Promise((resolve) => setTimeout(resolve, 2000));
                    refetch().catch(() => {});
                  } catch (err: any) {
                    error(
                      err?.response?.data?.cause || "Failed to initiate refill"
                    );
                  }
                }}
                onSave={async (newMetadata: SubscriptionMetadata) => {
                  // Note: For AI Hub, we may need different endpoint or approach
                  // This is a placeholder that may need backend support
                  try {
                    await axios.put(
                      "/accounts/products/ai-hub/subscription",
                      newMetadata
                    );
                    success("Settings saved successfully");
                    refetch().catch(() => {});
                  } catch (err: any) {
                    error(
                      err?.response?.data?.cause || "Failed to save settings"
                    );
                  }
                }}
              />
            )}
          </div>
        )}

        {/* Trial Upgrade Card */}
        {isTrialTier && (
          <Card className="sm:p-6 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="text-center">
              <h3 className="text-lg font-semibold text-indigo-900 mb-2">
                Ready to Scale?
              </h3>
              <p className="text-gray-600 mb-4">
                Upgrade to Professional or Enterprise to unlock auto-refill,
                higher token limits, and more features.
              </p>
              <Button
                text="View Upgrade Options"
                onClick={() =>
                  (window.location.href = `/manage-subscription?product=${productQuery}`)
                }
              />
            </div>
          </Card>
        )}

        {/* Support Card */}
        <Card className="sm:p-6">
          <div>
            <p className="font-medium">Support & Documentation</p>
            <span className="text-sm text-gray-500">
              Links for additional support
            </span>
          </div>

          <div className="mt-4">
            <dt className="text-sm font-medium text-gray-500">Documentation</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <a
                href="https://passio.gitbook.io/nutrition-ai"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                https://passio.gitbook.io/nutrition-ai
              </a>
            </dd>
          </div>
          <div className="mt-4">
            <dt className="text-sm font-medium text-gray-500">Email Support</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <a
                href="mailto:support@passiolife.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                support@passiolife.com
              </a>
            </dd>
          </div>
          <div className="mt-4">
            <dt className="text-sm font-medium text-gray-500">Slack Support</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <a
                href="https://passio-community.slack.com/channels/help"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                https://passio-community.slack.com/channels/help
              </a>
            </dd>
          </div>
        </Card>
      </div>
    </>
  );
};

export default DetailsTieredAIHub;
